import * as yup from "yup";
import { cpf } from "cpf-cnpj-validator";
import * as EmailValidator from "email-validator";

import { passwordValidationSchema } from "../../components/Password/schema";
import { validateCpf, validateEmail } from "./api";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ASYNC_DEBOUNCE_MS = 500;

const validatingListeners = { email: new Set(), cpf: new Set() };
const validatingCount = { email: 0, cpf: 0 };

const emitValidating = kind => {
  const active = validatingCount[kind] > 0;
  validatingListeners[kind].forEach(cb => cb(active));
};

const incValidating = kind => {
  validatingCount[kind] += 1;
  emitValidating(kind);
};

const decValidating = kind => {
  validatingCount[kind] = Math.max(0, validatingCount[kind] - 1);
  emitValidating(kind);
};

export const subscribeValidating = (kind, cb) => {
  if (!validatingListeners[kind]) return () => {};
  validatingListeners[kind].add(cb);
  cb(validatingCount[kind] > 0);
  return () => validatingListeners[kind].delete(cb);
};

const createDebouncedValidator = (apiCall, kind) => {
  const cache = new Map();
  const inflight = new Map();
  let latestValue = "";

  return value => {
    if (cache.has(value)) return Promise.resolve(cache.get(value));
    if (inflight.has(value)) return inflight.get(value);

    latestValue = value;
    incValidating(kind);
    const promise = new Promise(resolve => {
      setTimeout(async () => {
        if (latestValue !== value) {
          inflight.delete(value);
          decValidating(kind);
          resolve({ stale: true });
          return;
        }
        try {
          await apiCall(value);
          const result = { ok: true };
          cache.set(value, result);
          if (latestValue !== value) {
            resolve({ stale: true });
          } else {
            resolve(result);
          }
        } catch (error) {
          const result = { ok: false, status: error?.response?.status };
          cache.set(value, result);
          if (latestValue !== value) {
            resolve({ stale: true });
          } else {
            resolve(result);
          }
        } finally {
          inflight.delete(value);
          decValidating(kind);
        }
      }, ASYNC_DEBOUNCE_MS);
    });
    inflight.set(value, promise);
    return promise;
  };
};

const debouncedValidateEmail = createDebouncedValidator(validateEmail, "email");
const debouncedValidateCpf = createDebouncedValidator(validateCpf, "cpf");

export const REFERENCED_COUPONS = ["POA"];

export const isReferencedCoupon = code =>
  REFERENCED_COUPONS.includes(String(code || "").trim().toUpperCase());

const CEP_REGEX = /^\d{5}-\d{3}$/;

export const buildValidationSchema = ({
  disabledCreditCard,
  isPasswordlessCheckout,
  setUniqueEmail,
  setUniqueDocument,
  setShouldValidateDocument,
  shouldValidateDocument,
  isCepValid,
  showCupomReferenceInputs,
  // Modo infoprodutor: não nasce conta Jusfy, então "e-mail/CPF já cadastrado"
  // (consulta ao jusfy-backend) não se aplica — um cliente Jusfy pode assinar a
  // IA do infoprodutor com o mesmo e-mail/CPF. Duplicidade no tenant é o 409 do
  // addon-manager no submit. Só o formato continua validado.
  isInfoprodutoresMode = false,
}) => {
  const skipJusfyUniqueness = Boolean(isInfoprodutoresMode);

  let emailSchema = yup
    .string()
    .trim()
    .email("O email é inválido!")
    .required("O campo e-mail é obrigatório!");
  if (!skipJusfyUniqueness) {
    emailSchema = emailSchema.test(
      "unique-email",
      "Email já cadastrado",
      async function(email) {
        if (!email || !emailRegex.test(email)) {
          setUniqueEmail(null);
          return true;
        }
        if (!EmailValidator.validate(email)) {
          setUniqueEmail(null);
          return true;
        }
        const result = await debouncedValidateEmail(email);
        if (result.stale) return true;
        if (result.ok) {
          setUniqueEmail(null);
          return true;
        }
        if (result.status === 409) {
          setUniqueEmail(email);
          return false;
        }
        setUniqueEmail(null);
        return true;
      }
    );
  }

  let documentSchema = yup
    .string()
    .required("O campo CPF é obrigatório!")
    .test("isValidDocument", "O CPF é inválido.", value => cpf.isValid(value));
  if (!skipJusfyUniqueness) {
    documentSchema = documentSchema.test(
      "isUniqueDocument",
      "Já existe um usuário com este CPF.",
      async function(value) {
        if (value?.length !== 14) {
          setUniqueDocument && setUniqueDocument(false);
          return true;
        }
        const result = await debouncedValidateCpf(value);
        if (result.stale) return true;
        if (result.ok) {
          setShouldValidateDocument(false);
          setUniqueDocument && setUniqueDocument(false);
          return true;
        }
        if (result.status === 409) {
          setShouldValidateDocument(true);
          setUniqueDocument && setUniqueDocument(true);
          return false;
        }
        if (result.status === 422) {
          setShouldValidateDocument(true);
          setUniqueDocument && setUniqueDocument(false);
          return this.createError({ message: "O CPF é inválido." });
        }
        setUniqueDocument && setUniqueDocument(false);
        return true;
      }
    );
  }

  return yup.object().shape({
    name: yup.string().required("O campo nome é obrigatório!"),
    email: emailSchema,
    ...(!isPasswordlessCheckout
      ? {
          password: passwordValidationSchema.required("A senha é obrigatória!"),
          confirmation_password: yup
            .string()
            .oneOf([yup.ref("password"), null], "As senhas não coincidem!")
            .required("O campo confirmação é obrigatório!"),
        }
      : {}),
    document_number: documentSchema,
    phone: yup
      .string()
      .required("O campo telefone é obrigatório!")
      .matches(
        /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3}\-[0-9]{4}$/,
        "Telefone inválido."
      ),
    plan: yup
      .object()
      .nullable()
      .required("Escolha o plano!"),
    ...(!disabledCreditCard
      ? {
          cardholder_name: yup
            .string()
            .required("O nome do titular do cartão é obrigatório."),
          card_number: yup
            .string()
            .required("O número do cartão é obrigatório."),
          card_expiration: yup
            .string()
            .required("O mês/ano de expiração do cartão é obrigatório."),
          card_cvv: yup
            .string()
            .required("O código de segurança (CVV) do cartão é obrigatório."),
        }
      : {}),
    postal_code: yup
      .string()
      .required("O campo CEP é obrigatório!")
      .test("is-valid-cep", "CEP inválido.", value => {
        if (!value) return false;
        if (!CEP_REGEX.test(value)) return false;
        return isCepValid !== false;
      }),
    state: yup
      .object()
      .nullable()
      .required("O campo estado é obrigatório!"),
    city: yup
      .object()
      .nullable()
      .required("O campo cidade é obrigatório!"),
    district: yup.string().required("O campo bairro é obrigatório!"),
    address: yup.string().required("O campo endereço é obrigatório!"),
    address_number: yup.string().required("O campo número é obrigatório!"),
    address_complement: yup.string(),
    ...(showCupomReferenceInputs
      ? {
          reference_name: yup
            .string()
            .nullable()
            .required("O responsável pelo cupom é obrigatório"),
          reference_city: yup
            .string()
            .nullable()
            .required("A região responsável pelo cupom é obrigatória"),
        }
      : {}),
  });
};

export const CAPICANDIA_VARIANTS = {
  CAPICANDIA: "capicandia",
  CAPICANDIA_JUSFY: "capicandia_jusfy",
};

export const CAPICANDIA_COUPONS = {
  [CAPICANDIA_VARIANTS.CAPICANDIA]: {
    monthly: { code: "LIGHT_CAPICANDIA", amount: "97,00" },
    annually: { code: "LIGHT_CAPICANDIA_ANUAL", amount: "804,00" },
  },
  [CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY]: {
    monthly: { code: "STARTER_CAPICANDIA", amount: "116,90" },
    annually: { code: "STARTER_CAPICANDIA_ANUAL", amount: "1042,80" },
  },
};

export const CAPICANDIA_DISPLAY = {
  [CAPICANDIA_VARIANTS.CAPICANDIA]: { name: "Capicand-IA", order: 1 },
  [CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY]: { name: "Capicand-IA + Jusfy", order: 2 },
};

const normalizeCode = code => String(code || "").trim().toUpperCase();

const findCapicandiaEntry = code => {
  const upper = normalizeCode(code);
  if (!upper) return null;
  for (const variant of Object.keys(CAPICANDIA_COUPONS)) {
    for (const periodicity of Object.keys(CAPICANDIA_COUPONS[variant])) {
      if (CAPICANDIA_COUPONS[variant][periodicity].code === upper) {
        return { variant, periodicity };
      }
    }
  }
  return null;
};

export const isCapicandiaCoupon = code => !!findCapicandiaEntry(code);

// Cupons especiais Candia (oferta pontual): comportam-se como checkout Capicandia
// (redirect, modal de reativação) mas SEM o seletor de variantes. Podem ainda
// desabilitar o provisionamento (addon-manager) e usar label de reativação genérica.
export const CAPICANDIA_SPECIAL_COUPONS = {
  AULACANDIA_26: {
    variant: CAPICANDIA_VARIANTS.CAPICANDIA,
    periodicity: "monthly",
    provision: false,
    reactivateLabel: "Quero contratar nessa conta",
  },
  // Trial R$1 do Capicand-IA (spec 2026-09-04-experimente-capicandia). Provisiona
  // normalmente; o addon-manager marca o acesso como trial pelo nome do plano.
  EXPERIMENTE_CAPICANDIA: {
    variant: CAPICANDIA_VARIANTS.CAPICANDIA,
    periodicity: "monthly",
    provision: true,
    reactivateLabel: "Quero experimentar nessa conta",
  },
};

const findSpecialEntry = code =>
  CAPICANDIA_SPECIAL_COUPONS[normalizeCode(code)] || null;

export const isCapicandiaSpecialCoupon = code => !!findSpecialEntry(code);

// Define se o cupom deve notificar o addon-manager (provisionar Capicand-IA) ao
// final do cadastro. Especiais podem optar por não provisionar (provision:false).
export const shouldProvisionCapicandia = code => {
  const special = findSpecialEntry(code);
  if (special) return special.provision !== false;
  return isCapicandiaCoupon(code);
};

export const getCapicandiaReactivateLabel = code =>
  findSpecialEntry(code)?.reactivateLabel || "Quero a Capicand-IA nessa conta";

export const isCapicandiaCheckoutCoupon = code =>
  isCapicandiaCoupon(code) || isCapicandiaSpecialCoupon(code);

export const getCapicandiaVariant = code =>
  findCapicandiaEntry(code)?.variant || findSpecialEntry(code)?.variant || null;

export const getCapicandiaPeriodicity = code =>
  findCapicandiaEntry(code)?.periodicity || null;

export const getCapicandiaCheckoutPeriodicity = code =>
  findCapicandiaEntry(code)?.periodicity ||
  findSpecialEntry(code)?.periodicity ||
  null;

export const getCapicandiaForVariant = (variant, periodicity) =>
  CAPICANDIA_COUPONS[variant]?.[periodicity]?.code || "";

// Cupons de infoprodutores (IAs white-label — spec checkout-jusfy-cupom-trial).
// O cupom existe de verdade no jusfy-backend só para /coupons/validate passar;
// a copy do painel (nome, trial, preço) é FIXA aqui, não vem do cupom (D3).
// `app_url` é o subdomínio do infoprodutor ({slug}.BASE_DOMAIN), destino do
// aluno depois do cadastro. Fallback para produção, mesmo padrão do
// REACT_APP_CAPICANDIA_URL em useCheckoutFlow.
export const INFOPRODUTORES_COUPONS = {
  LAWLETTER: {
    plan_variant: "lawletter",
    product_name: "LawLetter IA",
    trial_days: 15,
    price_label: "R$ 115/mês",
    app_url:
      process.env.REACT_APP_INFOPRODUTORES_APP_URL_LAWLETTER ||
      "https://lawletter.jusfy.com.br",
  },
};

export const isInfoprodutoresCoupon = code =>
  Object.prototype.hasOwnProperty.call(
    INFOPRODUTORES_COUPONS,
    normalizeCode(code)
  );

export const getInfoprodutoresCoupon = code => {
  const upper = normalizeCode(code);
  if (!isInfoprodutoresCoupon(upper)) return null;
  return { code: upper, ...INFOPRODUTORES_COUPONS[upper] };
};

export const initialFormValues = {
  createdCustomerId: "",
  name: "",
  email: "",
  password: "",
  confirmation_password: "",
  coupom: "",
  document_number: "",
  phone: "",
  postal_code: "",
  city: "",
  state: "",
  address: "",
  address_number: "",
  address_complement: "",
  district: "",
  plan: null,
  cardholder_name: "",
  card_number: "",
  card_payment_method: null,
  card_cvv: "",
  card_expiration: "",
  recaptchaToken: "",
  recaptchaVersion: "",
  reference_name: null,
  reference_city: null,
  has_compra_garantida: false,
};
